import * as React from 'react';
import React__default from 'react';
import { Box, Typography, Dialog, DialogTitle, IconButton, DialogContent, DialogActions, Button, CircularProgress, Alert, AlertTitle, Grid, useTheme, Link, Avatar as Avatar$1, Paper, ClickAwayListener, MenuList, MenuItem, RadioGroup, FormControlLabel, Radio, Tooltip, List, ListItem, Tabs, Tab, Backdrop, Slider, ToggleButton, Menu, ListItemIcon, Checkbox, Select } from '@mui/material';
import { Close, Exclamation, Success, Info, ChevronRight, GeoWebLogo, Resize, useThemeContext, DragHandle, ArrowLeft, ArrowRight, KeyOption, ArrowUp, ArrowDown, KeyCommand, Options, Delete, VisibilityOff, Visibility } from '@opengeoweb/theme';
import { useFormContext } from 'react-hook-form';
import { isEmpty, errorMessages } from '@opengeoweb/form-fields';
import makeStyles from '@mui/styles/makeStyles';
import Draggable from 'react-draggable';
import { Resizable } from 're-resizable';
import { throttle } from 'lodash';
import withStyles from '@mui/styles/withStyles';
import createStyles from '@mui/styles/createStyles';

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var bgColors = {
  green: '#fcffeb',
  grey: '#f8f9f9',
  purple: '#f8f9f9',
  red: '#f9e8ea',
  yellow: '#fff8eb'
};
var borderColors = {
  green: '#72bb23',
  grey: '#9b9fb0',
  purple: '#9d9df2',
  red: '#f24a00',
  yellow: '#ffa800'
};

var tagStyle = function tagStyle(color) {
  // Set color to grey for summary
  var bgcolor = bgColors[color];
  var borderColor = borderColors[color];
  return {
    backgroundColor: bgcolor,
    m: 0,
    p: 0.5,
    border: "2px solid ".concat(borderColor),
    borderRadius: '4px',
    width: '85px',
    height: '20px',
    color: '#051039',
    maxHeight: '1em',
    boxSizing: 'content-box'
  };
};

var StatusTag = function StatusTag(_ref) {
  var content = _ref.content,
      color = _ref.color;
  return /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    sx: Object.assign({}, tagStyle(color)),
    "data-testid": "statusTag"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, content));
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var confirmStyle = {
  width: {
    xs: '100%',
    sm: '150px'
  },
  marginLeft: {
    xs: '0px!important',
    sm: '8px!important'
  },
  marginTop: {
    xs: 2,
    sm: 0
  }
};
var ConfirmationDialog = function ConfirmationDialog(_a) {
  var open = _a.open,
      title = _a.title,
      _a$description = _a.description,
      description = _a$description === void 0 ? 'Are you sure?' : _a$description,
      _a$confirmLabel = _a.confirmLabel,
      confirmLabel = _a$confirmLabel === void 0 ? 'Submit' : _a$confirmLabel,
      _a$cancelLabel = _a.cancelLabel,
      cancelLabel = _a$cancelLabel === void 0 ? 'Go back' : _a$cancelLabel,
      onSubmit = _a.onSubmit,
      _onClose = _a.onClose,
      _a$content = _a.content,
      content = _a$content === void 0 ? null : _a$content,
      _a$disableAutoFocus = _a.disableAutoFocus,
      disableAutoFocus = _a$disableAutoFocus === void 0 ? false : _a$disableAutoFocus,
      _a$isLoading = _a.isLoading,
      isLoading = _a$isLoading === void 0 ? false : _a$isLoading,
      other = __rest(_a, ["open", "title", "description", "confirmLabel", "cancelLabel", "onSubmit", "onClose", "content", "disableAutoFocus", "isLoading"]);

  other.catchOnCancel;
      var rest = __rest(other, ["catchOnCancel"]); // remove unwanted props


  return /*#__PURE__*/React.createElement(Dialog, Object.assign({
    "data-testid": "confirmationDialog",
    open: open,
    PaperProps: {
      sx: {
        width: '560px',
        margin: 2.5
      }
    },
    // fix for briefly showing default options when closing dialog
    transitionDuration: 0,
    onClose: function onClose() {
      _onClose('CLOSED');
    }
  }, rest), /*#__PURE__*/React.createElement(DialogTitle, {
    sx: {
      zIndex: 1,
      padding: 2,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: {
        xs: '16px',
        sm: '34px'
      },
      fontWeight: {
        xs: 500,
        sm: 400
      }
    },
    "data-testid": "confirmationDialog-title",
    component: "span"
  }, title), /*#__PURE__*/React.createElement(IconButton, {
    "data-testid": "confirmationDialog-close",
    "aria-label": "close",
    onClick: function onClick() {
      return _onClose('CLOSED');
    },
    size: "small"
  }, /*#__PURE__*/React.createElement(Close, {
    "data-testid": "CloseIcon"
  }))), /*#__PURE__*/React.createElement(DialogContent, {
    sx: {
      padding: '8px 16px'
    }
  }, description && /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, description), content), /*#__PURE__*/React.createElement(DialogActions, {
    sx: {
      padding: 2,
      flexDirection: {
        xs: 'column',
        sm: 'row'
      }
    }
  }, /*#__PURE__*/React.createElement(Button, {
    "data-testid": "confirmationDialog-cancel",
    variant: "tertiary",
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    sx: function sx(theme) {
      return {
        width: {
          xs: '100%',
          sm: '150px'
        },
        '&:focus': {
          outline: theme.palette.geowebColors.buttons.focusDefault.outline,
          borderColor: 'transparent'
        }
      };
    },
    onClick: function onClick() {
      return _onClose('CANCELLED');
    },
    disableFocusRipple: true
  }, cancelLabel), isLoading ? /*#__PURE__*/React.createElement(Box, {
    "data-testid": "confirm-dialog-spinner",
    sx: Object.assign(Object.assign({}, confirmStyle), {
      height: '40px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    })
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    size: 40,
    color: "secondary"
  })) : /*#__PURE__*/React.createElement(Button, {
    "data-testid": "confirmationDialog-confirm",
    variant: "primary",
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    sx: function sx(theme) {
      return Object.assign(Object.assign({}, confirmStyle), {
        '&:focus': {
          outline: theme.palette.geowebColors.buttons.focusDefault.outline,
          borderColor: 'transparent'
        }
      });
    },
    onClick: onSubmit,
    autoFocus: !disableAutoFocus,
    disableFocusRipple: true
  }, confirmLabel)));
};

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();

  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
        result;

    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toArray(arr) {
  return _arrayWithHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var showPrompt = function showPrompt(event) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line no-param-reassign
  event.returnValue = 'Changes that you made may not be saved.'; // changes are your browser have default text to overrule this
};
function usePreventBrowserClose(shouldPreventClose) {
  React.useEffect(function () {
    if (shouldPreventClose) {
      window.addEventListener('beforeunload', showPrompt);
    } else {
      window.removeEventListener('beforeunload', showPrompt);
    }

    return function () {
      window.removeEventListener('beforeunload', showPrompt);
    };
  }, [shouldPreventClose]);
}

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var usePoller = function usePoller(deps, callback) {
  var pollingInterval = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 300000;
  var interval = React.useRef(null);
  React.useEffect(function () {
    if (deps.filter(function (dep) {
      return dep !== null;
    }).length > 0) {
      if (interval.current) {
        clearInterval(interval.current);
      }

      interval.current = setInterval(function () {
        callback();
      }, pollingInterval);
    }

    return function () {
      if (interval.current) {
        clearInterval(interval.current);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deps]);
  return interval;
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
function usePrevious(value) {
  var ref = React.useRef();
  React.useEffect(function () {
    ref.current = value;
  });
  return ref.current;
}

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var useIsMounted = function useIsMounted() {
  var isMounted = React.useRef(false);
  React.useEffect(function () {
    isMounted.current = true;
    return function () {
      isMounted.current = false;
    };
  }, []);
  return {
    isMounted: isMounted
  };
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */

var HIDDEN_INPUT_HELPER_IS_DRAFT = 'IS_DRAFT';
var useDraftFormHelpers = function useDraftFormHelpers() {
  var isDefaultDraft = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

  var _useFormContext = useFormContext(),
      getValues = _useFormContext.getValues,
      setValue = _useFormContext.setValue,
      register = _useFormContext.register;

  React.useEffect(function () {
    setValue(HIDDEN_INPUT_HELPER_IS_DRAFT, isDefaultDraft); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  var isDraft = function isDraft() {
    return getValues(HIDDEN_INPUT_HELPER_IS_DRAFT) === true;
  };

  var isRequired = function isRequired(value) {
    if (!isDraft() && isEmpty(value)) {
      return errorMessages.required;
    }

    return true;
  };

  var toggleIsDraft = function toggleIsDraft(isDraft) {
    return setValue(HIDDEN_INPUT_HELPER_IS_DRAFT, isDraft);
  };

  var DraftFieldHelper = function DraftFieldHelper() {
    return /*#__PURE__*/React.createElement("input", Object.assign({}, register(HIDDEN_INPUT_HELPER_IS_DRAFT), {
      type: "hidden"
    }));
  };

  return {
    isRequired: isRequired,
    toggleIsDraft: toggleIsDraft,
    isDraft: isDraft,
    DraftFieldHelper: DraftFieldHelper
  };
};

var ConfirmationServiceContext = /*#__PURE__*/React.createContext(Promise.reject);
var useConfirmationDialog = function useConfirmationDialog() {
  return React.useContext(ConfirmationServiceContext);
};
var useDialogActions = function useDialogActions(awaitingPromiseRef) {
  var defaultOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

  var _useIsMounted = useIsMounted(),
      isMounted = _useIsMounted.isMounted;

  var _React$useState = React.useState(defaultOptions),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      confirmationState = _React$useState2[0],
      setConfirmationState = _React$useState2[1];

  var openConfirmation = function openConfirmation(options) {
    setConfirmationState(options);
    return new Promise(function (resolve, reject) {
      // since this is a React.ref it's safe to reassign it, although eslint says no
      // eslint-disable-next-line no-param-reassign
      awaitingPromiseRef.current = {
        resolve: resolve,
        reject: reject
      };
    });
  }; // clears state with timeout delay so optional values provided by the prop 'content' of ConfirmationDialog can be read


  var clearState = function clearState() {
    return setTimeout(function () {
      if (isMounted.current) {
        setConfirmationState(null);
      }
    }, 0);
  };

  var handleClose = function handleClose(reason) {
    if (confirmationState && confirmationState.catchOnCancel && awaitingPromiseRef.current) {
      awaitingPromiseRef.current.reject(reason);
    }

    clearState();
  };

  var handleSubmit = function handleSubmit() {
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve();
    }

    clearState();
  };

  return {
    openConfirmation: openConfirmation,
    confirmationState: confirmationState,
    handleSubmit: handleSubmit,
    handleClose: handleClose
  };
};
var ConfirmationServiceProvider = function ConfirmationServiceProvider(_ref) {
  var children = _ref.children;
  var awaitingPromiseRef = React.useRef();

  var _useDialogActions = useDialogActions(awaitingPromiseRef),
      openConfirmation = _useDialogActions.openConfirmation,
      confirmationState = _useDialogActions.confirmationState,
      handleSubmit = _useDialogActions.handleSubmit,
      handleClose = _useDialogActions.handleClose;

  return /*#__PURE__*/React.createElement(ConfirmationServiceContext.Provider, {
    value: openConfirmation
  }, children, /*#__PURE__*/React.createElement(ConfirmationDialog, Object.assign({
    open: Boolean(confirmationState),
    onSubmit: handleSubmit,
    onClose: handleClose
  }, confirmationState)));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */

var AlertIcon = function AlertIcon(_ref) {
  var _ref$severity = _ref.severity,
      severity = _ref$severity === void 0 ? 'error' : _ref$severity,
      _ref$dataTestId = _ref.dataTestId,
      dataTestId = _ref$dataTestId === void 0 ? 'alert-icon' : _ref$dataTestId;

  switch (severity) {
    case 'info':
      return /*#__PURE__*/React.createElement(Info, {
        sx: {
          color: 'geowebColors.functional.notification'
        },
        "data-testid": dataTestId
      });

    case 'success':
      return /*#__PURE__*/React.createElement(Success, {
        sx: {
          color: 'geowebColors.functional.success'
        },
        "data-testid": dataTestId
      });

    case 'warning':
      return /*#__PURE__*/React.createElement(Exclamation, {
        sx: {
          color: 'geowebColors.functional.warning'
        },
        "data-testid": dataTestId
      });

    case 'error':
    default:
      return /*#__PURE__*/React.createElement(Exclamation, {
        sx: {
          color: 'geowebColors.functional.error'
        },
        "data-testid": dataTestId
      });
  }
};

var AlertBanner = function AlertBanner(_ref) {
  var title = _ref.title,
      _ref$severity = _ref.severity,
      severity = _ref$severity === void 0 ? 'error' : _ref$severity,
      _ref$info = _ref.info,
      info = _ref$info === void 0 ? '' : _ref$info,
      _ref$dataTestId = _ref.dataTestId,
      dataTestId = _ref$dataTestId === void 0 ? 'alert-banner' : _ref$dataTestId,
      _ref$isCompact = _ref.isCompact,
      isCompact = _ref$isCompact === void 0 ? false : _ref$isCompact,
      _ref$shouldClose = _ref.shouldClose,
      shouldClose = _ref$shouldClose === void 0 ? false : _ref$shouldClose,
      _ref$actionButtonProp = _ref.actionButtonProps,
      actionButtonProps = _ref$actionButtonProp === void 0 ? undefined : _ref$actionButtonProp;

  var _React$useState = React.useState(true),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isOpen = _React$useState2[0],
      setIsOpen = _React$useState2[1];

  return isOpen && /*#__PURE__*/React.createElement(Alert, {
    "data-testid": dataTestId,
    severity: severity,
    icon: /*#__PURE__*/React.createElement(AlertIcon, {
      severity: severity
    }),
    action: shouldClose ? /*#__PURE__*/React.createElement(Button, {
      variant: "flat",
      onClick: function onClick() {
        return setIsOpen(false);
      },
      sx: {
        backgroundColor: 'transparent!important'
      }
    }, "CLOSE") : undefined,
    sx: isCompact ? {
      height: '32px',
      boxSizing: 'border-box',
      '& .MuiAlert-message': {
        padding: '0px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '& .MuiTypography-root': {
          display: 'inline'
        }
      },
      '& .MuiAlert-icon': {
        margin: '-3px 10px 0px 0px',
        padding: '0px'
      }
    } : {
      padding: '3px 16px',
      '& .MuiAlert-message': {
        padding: '8px 0'
      },
      '& .MuiAlert-icon': {
        padding: '8px 0'
      },
      '& .MuiAlert-action': {
        padding: 0
      }
    }
  }, /*#__PURE__*/React.createElement(AlertTitle, {
    sx: {
      fontSize: '14px',
      fontWeight: 'normal',
      marginTop: '0px!important',
      paddingTop: '0px!important',
      marginBottom: '0px!important'
    }
  }, title), info && /*#__PURE__*/React.createElement(Box, {
    sx: {
      fontSize: '16px',
      marginTop: 1,
      wordBreak: 'break-word'
    }
  }, info), actionButtonProps && /*#__PURE__*/React.createElement(Button, {
    variant: "flat",
    onClick: function onClick(event) {
      setIsOpen(false);
      actionButtonProps.onClick(event);
    },
    sx: {
      backgroundColor: 'transparent!important'
    },
    startIcon: /*#__PURE__*/React.createElement(ChevronRight, null)
  }, actionButtonProps.title.toUpperCase()));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var useStyles$6 = makeStyles(function () {
  return {
    wrapper: {
      height: '100vh',
      display: 'flex',
      flexDirection: 'column'
    },
    main: {
      flex: 1,
      position: 'relative'
    }
  };
});

var AppLayout = function AppLayout(_ref) {
  var header = _ref.header,
      children = _ref.children;
  var classes = useStyles$6();
  return /*#__PURE__*/React__default.createElement("div", {
    className: classes.wrapper
  }, header, /*#__PURE__*/React__default.createElement("main", {
    className: classes.main
  }, children));
};

var useStyles$5 = makeStyles(function (theme) {
  var _header;

  return {
    header: (_header = {
      height: 40,
      background: theme.palette.geowebColors.brand.brand,
      color: theme.palette.common.white,
      boxShadow: theme.shadows[1],
      zIndex: 1000
    }, _defineProperty(_header, theme.breakpoints.down('sm'), {
      padding: '0 8px'
    }), _defineProperty(_header, theme.breakpoints.up('sm'), {
      padding: '0 24px'
    }), _header),
    title: {
      marginLeft: 6,
      fontSize: '1.25rem',
      fontWeight: 400,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    },
    container: {
      height: '100%'
    }
  };
});

var AppHeader = function AppHeader(_ref) {
  var menu = _ref.menu,
      userMenu = _ref.userMenu,
      _ref$title = _ref.title,
      title = _ref$title === void 0 ? 'GeoWeb' : _ref$title;
  var classes = useStyles$5();
  return /*#__PURE__*/React__default.createElement("header", {
    className: classes.header
  }, /*#__PURE__*/React__default.createElement(Grid, {
    className: classes.container,
    container: true,
    alignItems: "center"
  }, /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    xs: 1,
    sm: 3
  }, /*#__PURE__*/React__default.createElement(Grid, {
    container: true,
    justifyContent: "flex-start"
  }, menu)), /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    xs: 10,
    sm: 6
  }, /*#__PURE__*/React__default.createElement(Grid, {
    container: true,
    justifyContent: "center",
    alignItems: "center",
    wrap: "nowrap"
  }, /*#__PURE__*/React__default.createElement(GeoWebLogo, null), /*#__PURE__*/React__default.createElement(Typography, {
    variant: "h1",
    className: classes.title
  }, title))), /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    xs: 1,
    sm: 3,
    container: true,
    justifyContent: "flex-end"
  }, userMenu)));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */

var DraggableComponent$1 = function DraggableComponent(_a) {
  var _a$bounds = _a.bounds,
      bounds = _a$bounds === void 0 ? 'body' : _a$bounds,
      children = _a.children,
      props = __rest(_a, ["bounds", "children"]);

  return /*#__PURE__*/React__default.createElement(Draggable, Object.assign({
    bounds: bounds
  }, props), children);
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */

var ResizeComponent = function ResizeComponent(_a) {
  var _a$startPosition = _a.startPosition,
      startPosition = _a$startPosition === void 0 ? {
    left: null,
    right: null,
    top: null
  } : _a$startPosition,
      children = _a.children,
      _a$onResizeStart = _a.onResizeStart,
      onResizeStart = _a$onResizeStart === void 0 ? function () {
    return null;
  } : _a$onResizeStart,
      size = _a.size,
      setSize = _a.setSize,
      _a$minHeight = _a.minHeight,
      minHeight = _a$minHeight === void 0 ? 50 : _a$minHeight,
      _a$minWidth = _a.minWidth,
      minWidth = _a$minWidth === void 0 ? 50 : _a$minWidth,
      props = __rest(_a, ["startPosition", "children", "onResizeStart", "size", "setSize", "minHeight", "minWidth"]);

  var theme = useTheme();
  var isRightAligned = startPosition && startPosition.right !== null && startPosition.right !== undefined;
  var onStartResize = React__default.useCallback(function (event, _direction, node) {
    event.stopPropagation();

    var _node$getBoundingClie = node.getBoundingClientRect(),
        width = _node$getBoundingClie.width,
        height = _node$getBoundingClie.height;

    setSize({
      width: width,
      height: height
    }); // fix for when initial maxHeight gets cleared
    // eslint-disable-next-line no-param-reassign

    node.style.height = "".concat(height, "px");
    onResizeStart();
  }, [onResizeStart, setSize]);
  var onResizeStop = React__default.useCallback(function (_event, _direction, _ref, delta) {
    var width = parseInt(size.width, 10) + delta.width;
    var height = parseInt(size.height, 10) + delta.height;
    setSize({
      width: width,
      height: height
    });
  }, [setSize, size]);
  return /*#__PURE__*/React__default.createElement(Resizable // TODO: implement bounds https://gitlab.com/opengeoweb/opengeoweb/-/issues/1035
  , Object.assign({
    // TODO: implement bounds https://gitlab.com/opengeoweb/opengeoweb/-/issues/1035
    minHeight: minHeight,
    minWidth: minWidth,
    size: size,
    onResizeStop: onResizeStop,
    handleComponent: {
      bottomRight: /*#__PURE__*/React__default.createElement(Resize, {
        style: {
          fill: "".concat(theme.palette.geowebColors.buttons.flat["default"].color)
        }
      }),
      bottomLeft: /*#__PURE__*/React__default.createElement(Resize, {
        isRightAligned: true,
        style: {
          fill: "".concat(theme.palette.geowebColors.buttons.flat["default"].color)
        }
      })
    },
    enable: {
      top: false,
      right: false,
      bottom: false,
      left: false,
      topRight: false,
      bottomRight: !isRightAligned,
      bottomLeft: isRightAligned,
      topLeft: false
    },
    onResizeStart: onStartResize
  }, props), children);
};

/**
 * ErrorBoundary
 * Wrap this component around parts of your application. It will catch any uncaught errors and display an alert, and allows you to continue using the rest of your application.
 *
 * @example
 * ``` <ErrorBoundary><ExampleComponent /></ErrorBoundary> ```
 */

var ErrorBoundary = /*#__PURE__*/function (_React$Component) {
  _inherits(ErrorBoundary, _React$Component);

  var _super = _createSuper(ErrorBoundary);

  function ErrorBoundary(props) {
    var _this;

    _classCallCheck(this, ErrorBoundary);

    _this = _super.call(this, props);
    _this.state = {
      error: null,
      errorInfo: null
    };
    return _this;
  }

  _createClass(ErrorBoundary, [{
    key: "componentDidCatch",
    value: function componentDidCatch(error, errorInfo) {
      var onError = this.props.onError;
      this.setState({
        error: error,
        errorInfo: errorInfo
      });
      onError(error, errorInfo);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$state = this.state,
          error = _this$state.error,
          errorInfo = _this$state.errorInfo;
      var children = this.props.children;

      if (error) {
        return /*#__PURE__*/React.createElement(Box, {
          sx: {
            whiteSpace: 'pre-wrap',
            width: 'inherit',
            height: 'inherit',
            overflowY: 'scroll'
          }
        }, /*#__PURE__*/React.createElement(AlertBanner, {
          severity: "error",
          title: "Woops, an error has occurred. Please report the error shown below and reload the page if needed.",
          info: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("br", null), error.toString(), /*#__PURE__*/React.createElement("br", null), errorInfo && errorInfo.componentStack, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Link, {
            underline: "always",
            style: {
              cursor: 'pointer'
            },
            onClick: function onClick() {
              return window.location.reload();
            }
          }, "Reload page"))
        }));
      }

      return children;
    }
  }], [{
    key: "getDerivedStateFromError",
    value: function getDerivedStateFromError(error) {
      return {
        error: error
      };
    }
  }]);

  return ErrorBoundary;
}(React.Component);
ErrorBoundary.defaultProps = {
  onError: function onError() {
    return null;
  }
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * */
var styles$3 = {
  small: {
    height: 16,
    width: 16,
    fontSize: '0.5rem'
  },
  large: {
    height: 32,
    width: 32,
    fontSize: '0.938rem'
  }
};

var Avatar = function Avatar(_ref) {
  var children = _ref.children,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'large' : _ref$size;
  return /*#__PURE__*/React.createElement(Avatar$1, {
    "data-testid": "avatar",
    sx: Object.assign(Object.assign({}, styles$3[size]), {
      fontWeight: 500,
      color: 'geowebColors.brand.brand',
      backgroundColor: 'common.white',
      border: '0.1px solid',
      borderColor: 'geowebColors.brand.brand'
    })
  }, children);
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var styles$2 = {
  container: {
    position: 'absolute',
    top: 38,
    right: 20,
    width: 256,
    borderRadius: 0,
    boxShadow: 5,
    backgroundColor: 'geowebColors.background.surface'
  },
  userMenu: {
    padding: 0,
    '& > li': {
      paddingTop: 3,
      paddingBottom: 3,
      paddingLeft: 2,
      paddingRight: 2
    }
  },
  button: {
    color: 'common.white',
    padding: 0
  }
};

var UserMenu = function UserMenu(_ref) {
  var _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      _ref$onToggle = _ref.onToggle,
      onToggle = _ref$onToggle === void 0 ? function () {
    return null;
  } : _ref$onToggle,
      _ref$onClose = _ref.onClose,
      onClose = _ref$onClose === void 0 ? function () {
    return null;
  } : _ref$onClose,
      children = _ref.children,
      initials = _ref.initials;
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(IconButton, {
    "data-testid": "userButton",
    onClick: onToggle,
    sx: styles$2.button,
    size: "large"
  }, /*#__PURE__*/React__default.createElement(Avatar, null, initials)), isOpen && /*#__PURE__*/React__default.createElement(Paper, {
    sx: styles$2.container,
    "data-testid": "userMenu"
  }, /*#__PURE__*/React__default.createElement(ClickAwayListener, {
    onClickAway: onClose,
    mouseEvent: "onMouseDown"
  }, /*#__PURE__*/React__default.createElement(MenuList, {
    sx: styles$2.userMenu,
    variant: "menu"
  }, children))));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */

var MenuItemTheme = function MenuItemTheme() {
  var _useThemeContext = useThemeContext(),
      isDark = _useThemeContext.isDark,
      toggleTheme = _useThemeContext.toggleTheme;

  return /*#__PURE__*/React__default.createElement(MenuItem, {
    divider: true
  }, /*#__PURE__*/React__default.createElement(Grid, {
    container: true
  }, /*#__PURE__*/React__default.createElement(Grid, {
    item: true
  }, "Choose your default app mode"), /*#__PURE__*/React__default.createElement(Grid, {
    item: true
  }, /*#__PURE__*/React__default.createElement(RadioGroup, {
    onChange: toggleTheme
  }, /*#__PURE__*/React__default.createElement(FormControlLabel, {
    value: true,
    control: /*#__PURE__*/React__default.createElement(Radio, {
      checked: !isDark
    }),
    label: "Light"
  }), /*#__PURE__*/React__default.createElement(FormControlLabel, {
    value: false,
    control: /*#__PURE__*/React__default.createElement(Radio, {
      checked: isDark
    }),
    label: "Dark"
  })))));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var useStyles$4 = makeStyles(function (theme) {
  return {
    footer: {
      boxShadow: theme.shadows[1],
      background: theme.palette.geowebColors.background.surface,
      textAlign: 'right',
      height: 18,
      zIndex: 1
    }
  };
}); // drag handles will be handled by DraggableResizable/Resizable

var ToolFooter = function ToolFooter() {
  var classes = useStyles$4();
  return /*#__PURE__*/React__default.createElement("footer", {
    className: classes.footer
  });
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */

var getStyle = function getStyle(size) {
  switch (size) {
    case 'xs':
      {
        return {
          fontSize: 10,
          fontWeight: 500
        };
      }

    case 'small':
      {
        return {
          fontSize: 16,
          fontWeight: 500,
          padding: '4px'
        };
      }

    case 'medium':
      {
        return {
          fontSize: 20,
          fontWeight: 400,
          padding: '8px'
        };
      }

    case 'large':
      {
        return {
          fontSize: 16,
          fontWeight: 500,
          padding: '12px'
        };
      }

    default:
      {
        return {};
      }
  }
};

var iconStyle = {
  color: 'geowebColors.greys.accessible',
  padding: 0,
  '&:hover': {
    background: 'action.hover'
  }
};

var ToolHeader = function ToolHeader(_ref) {
  var size = _ref.size,
      title = _ref.title,
      _ref$onClose = _ref.onClose,
      onClose = _ref$onClose === void 0 ? null : _ref$onClose,
      _ref$isDraggable = _ref.isDraggable,
      isDraggable = _ref$isDraggable === void 0 ? false : _ref$isDraggable,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$leftComponent = _ref.leftComponent,
      leftComponent = _ref$leftComponent === void 0 ? null : _ref$leftComponent;
  var styles = getStyle(size);
  var columnSizeIcon = leftComponent ? 3 : 1;
  var columnSizeTitle = leftComponent ? 6 : 10;
  return /*#__PURE__*/React__default.createElement(Box, {
    component: "header",
    className: className,
    sx: {
      boxShadow: 1,
      backgroundColor: 'geowebColors.background.surface',
      padding: styles.padding,
      zIndex: 2,
      cursor: isDraggable ? 'move' : null
    }
  }, /*#__PURE__*/React__default.createElement(Grid, {
    container: true,
    alignItems: "center"
  }, /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    xs: columnSizeIcon,
    sx: {
      paddingRight: 1
    }
  }, isDraggable && size !== 'xxs' && !leftComponent && /*#__PURE__*/React__default.createElement(Grid, {
    container: true,
    justifyContent: "flex-start"
  }, /*#__PURE__*/React__default.createElement(IconButton, {
    "data-testid": "dragBtn",
    disableRipple: true,
    size: "large",
    sx: Object.assign(Object.assign({}, iconStyle), {
      pointerEvents: 'none'
    })
  }, /*#__PURE__*/React__default.createElement(DragHandle, null))), leftComponent && leftComponent), /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    xs: columnSizeTitle
  }, /*#__PURE__*/React__default.createElement(Grid, {
    container: true,
    justifyContent: "center",
    alignItems: "center"
  }, title && /*#__PURE__*/React__default.createElement(Typography, {
    variant: "h2",
    noWrap: true,
    sx: {
      fontSize: styles.fontSize,
      fontWeight: styles.fontWeight
    }
  }, title), size === 'xxs' && /*#__PURE__*/React__default.createElement(IconButton, {
    "data-testid": "dragBtn-xxs",
    disableRipple: true,
    size: "small",
    sx: Object.assign(Object.assign({}, iconStyle), {
      pointerEvents: 'none',
      transform: 'rotate(90deg)',
      height: 12
    })
  }, /*#__PURE__*/React__default.createElement(DragHandle, null)))), /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    xs: columnSizeIcon,
    container: true,
    justifyContent: "flex-end"
  }, onClose && /*#__PURE__*/React__default.createElement(Tooltip, {
    title: "Close",
    placement: "bottom",
    sx: {
      pointerEvents: 'none'
    }
  }, /*#__PURE__*/React__default.createElement(IconButton, {
    "data-testid": "closeBtn",
    onClick: onClose,
    onTouchEnd: onClose,
    size: "large",
    sx: iconStyle
  }, /*#__PURE__*/React__default.createElement(Close, {
    "data-testid": "CloseIcon"
  }))))));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var styles$1 = {
  container: {
    overflow: 'hidden',
    borderRadius: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    position: 'relative'
  }
};
var BASE_ELEVATION = 5;

var ToolContainer = function ToolContainer(_a) {
  var _a$title = _a.title,
      title = _a$title === void 0 ? '' : _a$title,
      _a$size = _a.size,
      size = _a$size === void 0 ? 'medium' : _a$size,
      children = _a.children,
      onClose = _a.onClose,
      _a$isResizable = _a.isResizable,
      isResizable = _a$isResizable === void 0 ? false : _a$isResizable,
      _a$isDraggable = _a.isDraggable,
      isDraggable = _a$isDraggable === void 0 ? false : _a$isDraggable,
      _a$maxHeight = _a.maxHeight,
      maxHeight = _a$maxHeight === void 0 ? 'initial' : _a$maxHeight,
      _a$elevation = _a.elevation,
      elevation = _a$elevation === void 0 ? BASE_ELEVATION : _a$elevation,
      _a$headerClassName = _a.headerClassName,
      headerClassName = _a$headerClassName === void 0 ? '' : _a$headerClassName,
      _a$leftComponent = _a.leftComponent,
      leftComponent = _a$leftComponent === void 0 ? null : _a$leftComponent,
      props = __rest(_a, ["title", "size", "children", "onClose", "isResizable", "isDraggable", "maxHeight", "elevation", "headerClassName", "leftComponent"]);

  return /*#__PURE__*/React__default.createElement(Paper, Object.assign({
    elevation: elevation,
    sx: styles$1.container
  }, props), /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement(ToolHeader, {
    title: title,
    size: size,
    isDraggable: isDraggable,
    onClose: onClose,
    className: headerClassName,
    leftComponent: leftComponent
  })), /*#__PURE__*/React__default.createElement(Box, {
    sx: styles$1.content,
    style: {
      maxHeight: maxHeight
    }
  }, children), isResizable && /*#__PURE__*/React__default.createElement(ToolFooter, null));
};

var calculateDialogSizeAndPosition = function calculateDialogSizeAndPosition() {
  var width = window.innerWidth * 0.85;
  var height = window.innerHeight * 0.85;
  var top = window.innerHeight * 0.15 / 2;
  var left = window.innerWidth * 0.15 / 2;
  return {
    width: width,
    height: height,
    top: top,
    left: left
  };
};
var BASE_Z_INDEX = 100;

var DraggableComponent = function DraggableComponent(_a) {
  var title = _a.title,
      _a$bounds = _a.bounds,
      bounds = _a$bounds === void 0 ? 'body' : _a$bounds,
      _a$headerSize = _a.headerSize,
      headerSize = _a$headerSize === void 0 ? 'small' : _a$headerSize,
      _a$startSize = _a.startSize,
      startSize = _a$startSize === void 0 ? {
    width: 'auto',
    height: 'auto'
  } : _a$startSize,
      _a$startPosition = _a.startPosition,
      startPosition = _a$startPosition === void 0 ? {
    left: null,
    right: null,
    top: null
  } : _a$startPosition,
      children = _a.children,
      _a$isOpen = _a.isOpen,
      isOpen = _a$isOpen === void 0 ? true : _a$isOpen,
      _a$onClose = _a.onClose,
      onClose = _a$onClose === void 0 ? function () {
    return null;
  } : _a$onClose,
      _a$initialMaxHeight = _a.initialMaxHeight,
      defaultInitialMaxHeight = _a$initialMaxHeight === void 0 ? 'initial' : _a$initialMaxHeight,
      _a$onChangeSize = _a.onChangeSize,
      onChangeSize = _a$onChangeSize === void 0 ? function () {
    return null;
  } : _a$onChangeSize,
      _a$onMouseDown = _a.onMouseDown,
      onMouseDown = _a$onMouseDown === void 0 ? function () {
    return null;
  } : _a$onMouseDown,
      _a$order = _a.order,
      order = _a$order === void 0 ? 1 : _a$order,
      _a$source = _a.source,
      source = _a$source === void 0 ? 'app' : _a$source,
      _a$leftComponent = _a.leftComponent,
      leftComponent = _a$leftComponent === void 0 ? null : _a$leftComponent,
      props = __rest(_a, ["title", "bounds", "headerSize", "startSize", "startPosition", "children", "isOpen", "onClose", "initialMaxHeight", "onChangeSize", "onMouseDown", "order", "source", "leftComponent"]);

  var zIndex = (source === 'app' ? BASE_Z_INDEX : 1000) + order;
  var elevation = BASE_ELEVATION + order;
  var nodeRef = React__default.useRef(null);

  var _React$useState = React__default.useState({
    x: 0,
    y: 0
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      position = _React$useState2[0],
      setPosition = _React$useState2[1];

  var _React$useState3 = React__default.useState(startSize),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      size = _React$useState4[0],
      setSize = _React$useState4[1];

  var onStopDrag = React__default.useCallback(function (_event, position) {
    setPosition(position);
  }, [setPosition]);

  var _React$useState5 = React__default.useState(defaultInitialMaxHeight),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      initialMaxHeight = _React$useState6[0],
      setInitialMaxHeight = _React$useState6[1];

  var onStartResize = function onStartResize() {
    setInitialMaxHeight('initial');
  };

  var onResize = React__default.useCallback(function (_event, _handle, node) {
    var _node$getBoundingClie = node.getBoundingClientRect(),
        width = _node$getBoundingClie.width,
        height = _node$getBoundingClie.height;

    onChangeSize({
      width: width,
      height: height
    });
  }, [onChangeSize]);
  var onResizeWindow = React__default.useCallback(function (event) {
    if (!nodeRef.current) return;
    var bbox = nodeRef.current.firstChild.getBoundingClientRect();
    var windowTarget = event.currentTarget;
    if (!windowTarget) return;
    var innerHeight = windowTarget.innerHeight,
        innerWidth = windowTarget.innerWidth;
    var isHorizontallyLarger = bbox.width > innerWidth;
    var isVerticallyLarger = bbox.height > innerHeight; // left screen

    if (bbox.x < 0 || isHorizontallyLarger) {
      setPosition(Object.assign(Object.assign({}, position), {
        x: position.x - bbox.x
      }));
    } // right screen
    else if (bbox.x + bbox.width >= innerWidth) {
        var diffX = innerWidth - bbox.x - bbox.width;
        setPosition(Object.assign(Object.assign({}, position), {
          x: position.x + diffX
        }));
      } // top screen
      else if (bbox.y < 0 || isVerticallyLarger) {
          setPosition(Object.assign(Object.assign({}, position), {
            y: position.y - bbox.y
          }));
        } // bottom screen
        else if (bbox.y + bbox.height >= innerHeight) {
            var diffY = innerHeight - bbox.y - bbox.height;
            setPosition(Object.assign(Object.assign({}, position), {
              y: position.y + diffY
            }));
          }
  }, [position]);
  React__default.useEffect(function () {
    var throttledResize = throttle(onResizeWindow, 100);
    window.addEventListener('resize', throttledResize);
    return function () {
      window.removeEventListener('resize', throttledResize);
    };
  }, [onResizeWindow]);
  var toolcontainerId = React__default.useRef(Date.now().toString()).current;
  var headerClassName = "header-".concat(toolcontainerId);
  return isOpen ? /*#__PURE__*/React__default.createElement(DraggableComponent$1, {
    nodeRef: nodeRef,
    bounds: bounds,
    position: position,
    onStop: onStopDrag,
    handle: ".".concat(headerClassName),
    onMouseDown: onMouseDown
  }, /*#__PURE__*/React__default.createElement("div", {
    style: Object.assign({
      position: 'absolute',
      pointerEvents: 'all',
      zIndex: zIndex
    }, startPosition),
    ref: nodeRef
  }, /*#__PURE__*/React__default.createElement(ResizeComponent, Object.assign({}, props, {
    startPosition: startPosition,
    size: size,
    setSize: setSize,
    onResizeStart: onStartResize,
    onResize: onResize
  }), /*#__PURE__*/React__default.createElement(ToolContainer, {
    maxHeight: initialMaxHeight,
    onClose: onClose,
    title: title,
    size: headerSize,
    isDraggable: true,
    isResizable: true,
    elevation: elevation,
    headerClassName: headerClassName,
    leftComponent: leftComponent
  }, children)))) : null;
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var useStyles$3 = makeStyles(function (theme) {
  return {
    icon: {
      backgroundColor: theme.palette.geowebColors.textInputField["default"].rgba,
      width: 40,
      height: 40,
      display: 'inline-flex',
      verticalAlign: 'middle',
      alignItems: 'center',
      borderRadius: theme.shape.borderRadius,
      '&>span': {
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 500,
        display: 'flex',
        margin: 'auto',
        letterSpacing: 0.25
      }
    },
    segment: {
      margin: '0 2px',
      verticalAlign: 'middle',
      fontSize: 14,
      fontWeight: 500,
      letterSpacing: 0.25
    },
    connect: {
      fontSize: 18
    },
    divider: {
      margin: '0 6px',
      fontSize: 18
    }
  };
});

var ShortCutSegment = function ShortCutSegment(_ref) {
  var _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'text' : _ref$type,
      children = _ref.children;
  var classes = useStyles$3();

  if (type === 'divider') {
    return /*#__PURE__*/React__default.createElement(Typography, {
      "data-testid": "segment-divider",
      className: "".concat(classes.segment, " ").concat(classes.divider),
      component: "span"
    }, children);
  }

  if (type === 'icon') {
    return /*#__PURE__*/React__default.createElement("span", {
      "data-testid": "segment-icon",
      className: "".concat(classes.segment, " ").concat(classes.icon)
    }, /*#__PURE__*/React__default.createElement(Typography, {
      component: "span"
    }, children));
  }

  if (type === 'connect') {
    return /*#__PURE__*/React__default.createElement(Typography, {
      className: "".concat(classes.segment, " ").concat(classes.connect),
      "data-testid": "segment-connect",
      component: "span"
    }, children);
  }

  return /*#__PURE__*/React__default.createElement(Typography, {
    className: classes.segment,
    "data-testid": "segment-text",
    component: "span"
  }, children);
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var shortcuts = [{
  title: 'Choose a selected time',
  description: /*#__PURE__*/React__default.createElement(ShortCutSegment, null, "Left click on the time slider rail")
}, {
  title: 'Move backwards/forwards in selected time',
  description: /*#__PURE__*/React__default.createElement(ShortCutSegment, null, "Mouse scroll over the time slider rail")
}, {
  title: 'Move 1 step backwards/forwards in selected time',
  description: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "Ctrl"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(ArrowLeft, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "divider"
  }, "or"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(ArrowRight, null))),
  descriptionMac: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "Ctrl"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(KeyOption, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(ArrowLeft, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "divider"
  }, "or"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(ArrowRight, null)))
}, {
  title: 'Toggle hide/show time slider',
  description: /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "t")
}, {
  title: 'Change time slider scale (visible timespan)',
  description: /*#__PURE__*/React__default.createElement(ShortCutSegment, null, "Hold left click over Time Slider legend + drag left or right")
}, {
  title: 'Expand/shrink time slider scale (visible timespan)',
  description: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "Ctrl"), /*#__PURE__*/React__default.createElement(ShortCutSegment, null, "+ Scroll the slider rail"))
}, {
  title: 'Mouseover to change selected time',
  description: /*#__PURE__*/React__default.createElement(ShortCutSegment, null, "Hover over Time Slider rail or time indicator")
}, {
  title: 'Mouseover switch on/off',
  description: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "Ctrl"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "Alt"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "h")),
  descriptionMac: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "Ctrl"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(KeyOption, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "h"))
}, {
  title: 'Change time step or animation speed',
  description: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "Ctrl"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(ArrowUp, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "divider"
  }, "or"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(ArrowDown, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, null, "(when slider active)")),
  descriptionMac: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(KeyCommand, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(ArrowUp, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "divider"
  }, "or"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(ArrowDown, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, null, "(when slider active)"))
}, {
  title: 'Return to current time',
  description: /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "Home"),
  descriptionMac: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "fn"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "connect"
  }, "+"), /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(ArrowLeft, null)))
}, {
  title: 'Change layer manager values by scrolling',
  description: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, "Ctrl"), /*#__PURE__*/React__default.createElement(ShortCutSegment, null, " + Mouse scroll")),
  descriptionMac: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(ShortCutSegment, {
    type: "icon"
  }, /*#__PURE__*/React__default.createElement(KeyCommand, null)), /*#__PURE__*/React__default.createElement(ShortCutSegment, null, " + Mouse scroll"))
}];
var getShortcutList = function getShortcutList(shortcutList) {
  var platform = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'windows';
  return shortcutList.map(function (_ref) {
    var title = _ref.title,
        description = _ref.description,
        descriptionMac = _ref.descriptionMac;
    return Object.assign({
      title: title,
      description: description
    }, platform === 'windows' ? {
      description: description
    } : {
      description: descriptionMac || description
    });
  });
};
var useStyles$2 = makeStyles(function () {
  return {
    row: {
      minHeight: 56
    },
    title: {
      fontSize: 12,
      fontWeight: 300,
      letterSpacing: 0.4,
      opacity: 0.67
    },
    description: {
      alignItems: 'center'
    }
  };
});

var ShortcutList = function ShortcutList(_ref2) {
  var _ref2$platform = _ref2.platform,
      platform = _ref2$platform === void 0 ? 'windows' : _ref2$platform;
  var classes = useStyles$2();
  var rows = getShortcutList(shortcuts, platform);
  return /*#__PURE__*/React__default.createElement(List, {
    "data-testid": "list-".concat(platform)
  }, rows.map(function (row, index) {
    return /*#__PURE__*/React__default.createElement(ListItem, {
      className: classes.row,
      divider: index !== rows.length - 1,
      key: row.title
    }, /*#__PURE__*/React__default.createElement(Grid, {
      container: true,
      direction: "row",
      justifyContent: "space-between",
      alignItems: "center"
    }, /*#__PURE__*/React__default.createElement(Grid, {
      item: true,
      className: classes.title
    }, row.title), /*#__PURE__*/React__default.createElement(Grid, {
      item: true
    }, /*#__PURE__*/React__default.createElement("div", {
      className: classes.description
    }, row.description))));
  }));
};

var TabPanel = function TabPanel(_ref) {
  var children = _ref.children,
      value = _ref.value,
      index = _ref.index;
  return /*#__PURE__*/React__default.createElement("div", {
    role: "tabpanel",
    hidden: value !== index,
    id: "tabpanel-".concat(index),
    "aria-labelledby": "tab-".concat(index)
  }, value === index && children);
};

var styles = {
  container: {
    margin: '0px 20px'
  },
  tab: {
    fontSize: 12
  }
};

var CheatSheet = function CheatSheet() {
  var _React$useState = React__default.useState(0),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      activeTab = _React$useState2[0],
      onChangeActiveTab = _React$useState2[1];

  var onChangeTab = function onChangeTab(_event, newValue) {
    onChangeActiveTab(newValue);
  };

  return /*#__PURE__*/React__default.createElement(Box, {
    sx: styles.container
  }, /*#__PURE__*/React__default.createElement(Tabs, {
    value: activeTab,
    onChange: onChangeTab,
    "aria-label": "platform"
  }, /*#__PURE__*/React__default.createElement(Tab, {
    sx: styles.tab,
    label: "Windows"
  }), /*#__PURE__*/React__default.createElement(Tab, {
    sx: styles.tab,
    label: "Mac"
  })), /*#__PURE__*/React__default.createElement(TabPanel, {
    value: activeTab,
    index: 0
  }, /*#__PURE__*/React__default.createElement(ShortcutList, null)), /*#__PURE__*/React__default.createElement(TabPanel, {
    value: activeTab,
    index: 1
  }, /*#__PURE__*/React__default.createElement(ShortcutList, {
    platform: "mac"
  })));
};

var useStyles$1 = makeStyles(function () {
  return {
    backdrop: {
      zIndex: 1
    }
  };
});

var UserMenuItemCheatSheet = function UserMenuItemCheatSheet() {
  var classes = useStyles$1();

  var _React$useState = React__default.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      open = _React$useState2[0],
      setOpen = _React$useState2[1];

  var onClose = function onClose() {
    setOpen(false);
  };

  var onOpen = function onOpen() {
    setOpen(true);
  };

  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(MenuItem, {
    "data-testid": "cheatsheet-btn",
    onClick: onOpen,
    divider: true
  }, /*#__PURE__*/React__default.createElement(Grid, {
    container: true
  }, /*#__PURE__*/React__default.createElement(Grid, {
    item: true
  }, "Cheat sheet"))), /*#__PURE__*/React__default.createElement(Backdrop, {
    "data-testid": "cheatsheet-content",
    open: open,
    className: classes.backdrop
  }, /*#__PURE__*/React__default.createElement(DraggableComponent, {
    startSize: {
      width: 700
    },
    minHeight: 300,
    minWidth: 500,
    title: "Cheat sheet",
    headerSize: "small",
    isOpen: true,
    onClose: onClose,
    bounds: "parent"
  }, /*#__PURE__*/React__default.createElement(CheatSheet, null))));
};

var useUserMenu = function useUserMenu() {
  var open = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

  var _React$useState = React__default.useState(open),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      isOpen = _React$useState2[0],
      setOpen = _React$useState2[1];

  var onClose = function onClose() {
    return setOpen(false);
  };

  var onToggle = function onToggle() {
    return setOpen(!isOpen);
  };

  return {
    isOpen: isOpen,
    onClose: onClose,
    onToggle: onToggle
  };
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var getInitials = function getInitials(name) {
  if (!name) return '';
  var nameWithoutEmail = name.split('@')[0];
  var array = nameWithoutEmail.split('.');

  switch (array.length) {
    case 1:
      return array[0].charAt(0).toUpperCase();

    default:
      return array[0].charAt(0).toUpperCase() + array[array.length - 1].charAt(0).toUpperCase();
  }
};

var sliderStyles = makeStyles(function (theme) {
  return {
    sliderHeader: {
      fontFamily: 'Roboto-Black, Roboto',
      fontSize: 12,
      color: theme.palette.geowebColors.typographyAndIcons.text,
      padding: '11px 0 11px 16px'
    }
  };
});
var CustomSliderComponent = withStyles(function (theme) {
  return {
    root: {
      // highlight selected label
      '& span.selectedLabel': {
        fontWeight: 'bold'
      }
    },
    thumb: {
      backgroundColor: theme.palette.geowebColors.customSlider.thumb,
      '$disabled &': {
        backgroundColor: theme.palette.geowebColors.customSlider.thumbDisabled
      },
      boxShadow: '0px 3px 5px 1px rgba(0,0,0,0.3)',
      '&.Mui-focusVisible, &:hover, &:active': {
        boxShadow: '0px 3px 5px 2px rgba(0,0,0,0.4)'
      }
    },
    mark: {
      backgroundColor: theme.palette.geowebColors.customSlider.mark,
      '$disabled &': {
        backgroundColor: theme.palette.geowebColors.customSlider.markDisabled
      }
    },
    markLabel: {
      fontFamily: 'Roboto-Black, Roboto',
      fontSize: 12,
      color: theme.palette.geowebColors.typographyAndIcons.text
    },
    track: {
      color: theme.palette.geowebColors.customSlider.track,
      backgroundColor: theme.palette.geowebColors.customSlider.track,
      '$disabled &': {
        backgroundColor: theme.palette.geowebColors.customSlider.trackDisabled,
        color: theme.palette.geowebColors.customSlider.trackDisabled
      }
    },
    rail: {
      backgroundColor: theme.palette.geowebColors.customSlider.rail,
      opacity: 1,
      '$disabled &': {
        backgroundColor: theme.palette.geowebColors.customSlider.railDisabled,
        opacity: 1
      }
    },
    disabled: {// this has to exist, because otherwise '$disabled &' rules above has no effect ???
    }
  };
})(Slider);
var CustomSlider = function CustomSlider(_a) {
  var value = _a.value,
      _a$orientation = _a.orientation,
      orientation = _a$orientation === void 0 ? 'vertical' : _a$orientation,
      _a$shouldAutoFocus = _a.shouldAutoFocus,
      shouldAutoFocus = _a$shouldAutoFocus === void 0 ? false : _a$shouldAutoFocus,
      props = __rest(_a, ["value", "orientation", "shouldAutoFocus"]); // Add custom class (for bold text) to label adjacent to thumb component.
  // Material UI doesn't support highlighing only adjacent label
  // when track is highlighted everywhere below selected value.


  var element = React.useRef(null);
  var themeContext = useThemeContext();
  React.useEffect(function () {
    var activeMarkLabelElements = element.current.getElementsByClassName('MuiSlider-markLabelActive');

    var _reverse = _toConsumableArray(activeMarkLabelElements).reverse(),
        _reverse2 = _toArray(_reverse),
        first = _reverse2[0],
        rest = _reverse2.slice(1);

    if (first) first.classList.add('selectedLabel');
    rest.forEach(function (element) {
      return element.classList.remove('selectedLabel');
    });
  }, [value, themeContext.isDark]); // autofocus

  React.useEffect(function () {
    if (shouldAutoFocus && element.current) {
      var thumb = element.current.querySelector('.MuiSlider-thumb input');
      thumb.focus();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  return /*#__PURE__*/React.createElement(CustomSliderComponent, Object.assign({
    ref: element,
    value: value,
    orientation: orientation
  }, props));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */

var CustomToggleButton = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var _a$variant = _a.variant,
      variant = _a$variant === void 0 ? 'primary' : _a$variant,
      className = _a.className,
      _a$value = _a.value,
      value = _a$value === void 0 ? 0 : _a$value,
      props = __rest(_a, ["variant", "className", "value"]);

  return /*#__PURE__*/React.createElement(ToggleButton, Object.assign({
    ref: ref,
    className: "".concat(variant, " ").concat(className),
    value: value
  }, props));
});

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var ToolButton = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var active = _a.active,
      _a$width = _a.width,
      width = _a$width === void 0 ? '24px' : _a$width,
      _a$height = _a.height,
      height = _a$height === void 0 ? '24px' : _a$height,
      children = _a.children,
      _a$value = _a.value,
      value = _a$value === void 0 ? 0 : _a$value,
      _a$sx = _a.sx,
      sx = _a$sx === void 0 ? {} : _a$sx,
      _a$variant = _a.variant,
      variant = _a$variant === void 0 ? 'tool' : _a$variant,
      props = __rest(_a, ["active", "width", "height", "children", "value", "sx", "variant"]);

  return /*#__PURE__*/React.createElement(CustomToggleButton, Object.assign({
    ref: ref,
    variant: variant,
    disableRipple: true,
    selected: active,
    value: value,
    sx: Object.assign({
      width: "".concat(width, "!important"),
      height: "".concat(height, "!important"),
      textTransform: 'none!important',
      padding: '11px!important',
      '& .MuiSvgIcon-root': {
        marginRight: '0px!important'
      }
    }, sx)
  }, props), children);
});

var getPosition = function getPosition(menuPosition) {
  switch (menuPosition) {
    case 'bottom':
      return {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      };

    default:
    case 'left':
      return {
        anchorOrigin: {
          vertical: 'center',
          horizontal: 'left'
        },
        transformOrigin: {
          vertical: 'center',
          horizontal: 'right'
        }
      };

    case 'right':
      return {
        anchorOrigin: {
          vertical: 'center',
          horizontal: 'right'
        },
        transformOrigin: {
          vertical: 'center',
          horizontal: 'left'
        }
      };
  }
};
var ToggleMenu = function ToggleMenu(_ref) {
  var _ref$buttonTestId = _ref.buttonTestId,
      buttonTestId = _ref$buttonTestId === void 0 ? 'toggleMenuButton' : _ref$buttonTestId,
      _ref$menuPosition = _ref.menuPosition,
      menuPosition = _ref$menuPosition === void 0 ? 'left' : _ref$menuPosition,
      menuItems = _ref.menuItems,
      children = _ref.children,
      buttonSx = _ref.buttonSx,
      _ref$tooltipTitle = _ref.tooltipTitle,
      tooltipTitle = _ref$tooltipTitle === void 0 ? '' : _ref$tooltipTitle,
      variant = _ref.variant,
      _ref$isDefaultOpen = _ref.isDefaultOpen,
      isDefaultOpen = _ref$isDefaultOpen === void 0 ? false : _ref$isDefaultOpen;

  var _React$useState = React.useState(null),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      anchorEl = _React$useState2[0],
      setAnchorEl = _React$useState2[1];

  var isOpen = Boolean(anchorEl);

  var _getPosition = getPosition(menuPosition),
      anchorOrigin = _getPosition.anchorOrigin,
      transformOrigin = _getPosition.transformOrigin;

  var handleClick = function handleClick(event) {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  var handleClose = function handleClose(event) {
    event.stopPropagation();
    setAnchorEl(null);
  };

  React.useEffect(function () {
    if (isDefaultOpen) {
      setAnchorEl(document.getElementById(buttonTestId));
    }
  }, [isDefaultOpen, buttonTestId]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Tooltip, {
    placement: "top",
    title: tooltipTitle
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(ToolButton, {
    active: isOpen,
    sx: buttonSx,
    onClick: handleClick,
    "data-testid": buttonTestId,
    variant: variant,
    id: buttonTestId
  }, /*#__PURE__*/React.createElement(Options, null)))), /*#__PURE__*/React.createElement(Menu, {
    anchorEl: anchorEl,
    open: isOpen,
    onClose: handleClose,
    anchorOrigin: anchorOrigin,
    transformOrigin: transformOrigin,
    BackdropProps: {
      style: {
        opacity: 0,
        transition: 'none'
      }
    },
    MenuListProps: children && {
      style: {
        padding: '0px'
      }
    }
  }, menuItems && menuItems.map(function (menuItem) {
    var text = menuItem.text,
        icon = menuItem.icon,
        action = menuItem.action;
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: text,
      onClick: function onClick(event) {
        action(event);
        handleClose(event);
      }
    }, icon && /*#__PURE__*/React.createElement(ListItemIcon, null, icon), /*#__PURE__*/React.createElement(Typography, null, text));
  }), children));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * */
var useStyles = makeStyles(function (theme) {
  return createStyles({
    button: {
      height: 24,
      width: 24,
      borderRadius: 5,
      marginTop: 4,
      color: function color(_ref) {
        var isActive = _ref.isActive,
            isAccessible = _ref.isAccessible,
            isEnabled = _ref.isEnabled;

        if (!isEnabled) {
          return theme.palette.geowebColors.buttons.flat.disabled.color;
        }

        if (isAccessible) {
          return theme.palette.geowebColors.greys.accessible;
        }

        if (isActive) {
          return theme.palette.common.white;
        }

        return theme.palette.geowebColors.buttons.flat["default"].color;
      },
      backgroundColor: function backgroundColor(_ref2) {
        var isActive = _ref2.isActive;
        return isActive ? theme.palette.geowebColors.buttons.primary.active.fill : 'inherit';
      },
      '&:focus': {
        backgroundColor: function backgroundColor(_ref3) {
          var isActive = _ref3.isActive;
          return isActive ? theme.palette.geowebColors.buttons.primary.mouseOver.fill : theme.palette.geowebColors.buttons.flat.mouseOver.fill;
        },
        outline: theme.palette.geowebColors.buttons.focusDefault.outline,
        borderRadius: 0
      },
      '&:hover': {
        backgroundColor: function backgroundColor(_ref4) {
          var isActive = _ref4.isActive;
          return isActive ? theme.palette.geowebColors.buttons.primary.mouseOver.fill : theme.palette.geowebColors.buttons.flat.mouseOver.fill;
        }
      }
    },
    popper: {
      pointerEvents: 'none'
    }
  });
});
var ManagerButton = function ManagerButton(_a) {
  var _a$tooltipTitle = _a.tooltipTitle,
      tooltipTitle = _a$tooltipTitle === void 0 ? '' : _a$tooltipTitle,
      _a$onClick = _a.onClick,
      onClick = _a$onClick === void 0 ? function () {} : _a$onClick,
      _a$isActive = _a.isActive,
      isActive = _a$isActive === void 0 ? false : _a$isActive,
      _a$isEnabled = _a.isEnabled,
      isEnabled = _a$isEnabled === void 0 ? true : _a$isEnabled,
      icon = _a.icon,
      testId = _a.testId,
      _a$isAccessible = _a.isAccessible,
      isAccessible = _a$isAccessible === void 0 ? false : _a$isAccessible,
      className = _a.className,
      props = __rest(_a, ["tooltipTitle", "onClick", "isActive", "isEnabled", "icon", "testId", "isAccessible", "className"]);

  var classes = useStyles({
    isActive: isActive,
    isEnabled: isEnabled,
    isAccessible: isAccessible
  });

  var handleClick = function handleClick(event) {
    onClick(event);
  };

  return /*#__PURE__*/React.createElement(Tooltip, {
    title: tooltipTitle,
    placement: "top",
    classes: {
      popper: classes.popper
    }
  }, /*#__PURE__*/React.createElement(IconButton, Object.assign({
    size: "small",
    onClick: handleClick,
    "data-testid": testId,
    className: "".concat(classes.button, " ").concat(className),
    disableFocusRipple: true
  }, props), icon));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var ManagerDeleteButton = function ManagerDeleteButton(_ref) {
  var tooltipTitle = _ref.tooltipTitle,
      onClickDelete = _ref.onClickDelete,
      _ref$isEnabled = _ref.isEnabled,
      isEnabled = _ref$isEnabled === void 0 ? true : _ref$isEnabled;
  return /*#__PURE__*/React.createElement(ManagerButton, {
    tooltipTitle: tooltipTitle,
    onClick: onClickDelete,
    icon: /*#__PURE__*/React.createElement(Delete, null),
    testId: "deleteButton",
    isEnabled: isEnabled
  });
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var EnableButton = function EnableButton(_ref) {
  var isEnabled = _ref.isEnabled,
      title = _ref.title,
      onChangeEnableLayer = _ref.onChangeEnableLayer;

  var onClick = function onClick() {
    onChangeEnableLayer(!isEnabled);
  };

  return /*#__PURE__*/React.createElement(ManagerButton, {
    tooltipTitle: title,
    onClick: onClick,
    icon: /*#__PURE__*/React.createElement(Checkbox, {
      sx: {
        color: 'inherit',
        padding: 0.5,
        borderRadius: 1.25,
        '&.Mui-focusVisible': {
          backgroundColor: 'geowebColors.buttons.tool.mouseOver.fill'
        },
        '&:hover': {
          backgroundColor: 'transparent!important'
        },
        '&.Mui-checked': {
          color: 'inherit'
        }
      },
      icon: /*#__PURE__*/React.createElement(VisibilityOff, null),
      checkedIcon: /*#__PURE__*/React.createElement(Visibility, null),
      checked: isEnabled,
      disableFocusRipple: true,
      tabIndex: -1
    }),
    testId: "enableButton",
    isEnabled: isEnabled
  });
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var tooltipContainerStyles = function tooltipContainerStyles(isEnabled) {
  var hasBackgroundColor = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  return Object.assign({
    width: '100%',
    height: 32,
    fontWeight: 500,
    fontSize: function fontSize(theme) {
      return theme.palette.geowebColors.layerManager.tableRowDefaultText.fontSize;
    },
    color: isEnabled ? 'geowebColors.layerManager.tableRowDefaultText.rgba' : 'geowebColors.layerManager.tableRowDisabledText.rgba',
    '& .MuiOutlinedInput-input': {
      padding: '6px 24px 6px 8px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent!important'
    },
    '&:hover .MuiSelect-select': {
      outline: function outline(theme) {
        return theme.palette.geowebColors.buttons.focusDefault.outline;
      }
    },
    '&.Mui-focused .MuiSelect-select': {
      outline: function outline(theme) {
        return theme.palette.geowebColors.buttons.focusDefault.outline;
      }
    },
    '&:focus&.MuiButton-root': {
      outline: function outline(theme) {
        return theme.palette.geowebColors.buttons.focusDefault.outline;
      }
    },
    '& .MuiSvgIcon-root': {
      transition: 'none',
      fill: function fill(theme) {
        return isEnabled ? theme.palette.geowebColors.buttons.flat["default"].color : theme.palette.geowebColors.buttons.flat.disabled.color;
      }
    },
    '&>div': {
      paddingLeft: 1
    }
  }, hasBackgroundColor && backgroundColor);
};
var backgroundColor = {
  '&:hover .MuiSelect-select': {
    outline: function outline(theme) {
      return theme.palette.geowebColors.buttons.focusDefault.outline;
    },
    backgroundColor: 'geowebColors.buttons.tool.mouseOver.fill'
  },
  '&.Mui-focused .MuiSelect-select': {
    backgroundColor: 'geowebColors.buttons.tool.mouseOver.fill'
  },
  '&:hover&.MuiButton-root': {
    backgroundColor: 'geowebColors.buttons.tool.mouseOver.fill'
  },
  '&:focus&.MuiButton-root': {
    outline: function outline(theme) {
      return theme.palette.geowebColors.buttons.focusDefault.outline;
    },
    backgroundColor: 'geowebColors.buttons.tool.mouseOver.fill'
  }
};

var TooltipSelect = function TooltipSelect(_a) {
  var tooltip = _a.tooltip,
      _a$list = _a.list,
      list = _a$list === void 0 ? [] : _a$list,
      _a$currentIndex = _a.currentIndex,
      currentIndex = _a$currentIndex === void 0 ? -1 : _a$currentIndex,
      _a$onChangeMouseWheel = _a.onChangeMouseWheel,
      onChangeMouseWheel = _a$onChangeMouseWheel === void 0 ? function () {} : _a$onChangeMouseWheel,
      _a$requiresCtrlToChan = _a.requiresCtrlToChange,
      requiresCtrlToChange = _a$requiresCtrlToChan === void 0 ? false : _a$requiresCtrlToChan,
      isEnabled = _a.isEnabled,
      noStyling = _a.noStyling,
      hasBackgroundColor = _a.hasBackgroundColor,
      props = __rest(_a, ["tooltip", "list", "currentIndex", "onChangeMouseWheel", "requiresCtrlToChange", "isEnabled", "noStyling", "hasBackgroundColor"]);

  props.disableUnderline;
      var otherProps = __rest(props, ["disableUnderline"]);

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      selectOpen = _React$useState2[0],
      setSelectOpen = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      tooltipOpen = _React$useState4[0],
      setTooltipOpen = _React$useState4[1];

  var onWheel = React.useCallback(function (event) {
    if (requiresCtrlToChange && (event.ctrlKey || event.metaKey) || !requiresCtrlToChange) {
      if (selectOpen) return;
      var direction = event.deltaY < 0 ? 1 : -1;
      var newValue = list[Math.min(Math.max(currentIndex - direction, 0), list.length - 1)];
      onChangeMouseWheel(newValue);
    }
  }, [selectOpen, currentIndex, list, onChangeMouseWheel, requiresCtrlToChange]);
  var onOpen = React.useCallback(function () {
    setSelectOpen(true);
    setTooltipOpen(false);
  }, []);
  var onClose = React.useCallback(function () {
    setSelectOpen(false);
  }, []);
  var onMouseEnter = React.useCallback(function () {
    if (!selectOpen) setTooltipOpen(true);
  }, [selectOpen]);
  var onMouseLeave = React.useCallback(function () {
    setTooltipOpen(false);
  }, []);
  var onFocus = React.useCallback(function () {
    setTooltipOpen(true);
  }, []);
  var onBlur = React.useCallback(function () {
    setTooltipOpen(false);
  }, []);
  return /*#__PURE__*/React.createElement(Tooltip, {
    title: tooltip,
    placement: "top",
    open: tooltipOpen,
    sx: noStyling ? undefined : tooltipContainerStyles(isEnabled, hasBackgroundColor)
  }, /*#__PURE__*/React.createElement(Select, Object.assign({
    onOpen: onOpen,
    onClose: onClose,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onFocus: onFocus,
    onBlur: onBlur,
    onWheel: onWheel,
    MenuProps: {
      BackdropProps: {
        sx: {
          '&.MuiBackdrop-root': {
            backgroundColor: 'transparent'
          }
        }
      },
      sx: {
        '& ul': {
          backgroundColor: 'geowebColors.background.surface'
        },
        '& li': {
          fontSize: '16px',
          padding: '12px',
          '&:hover': {
            backgroundColor: 'geowebColors.buttons.tool.mouseOver.fill'
          },
          '&.Mui-selected': {
            color: 'geowebColors.buttons.tool.active.color',
            backgroundColor: 'geowebColors.buttons.tool.active.fill',
            boxShadow: "none",
            '&:hover': {
              backgroundColor: 'geowebColors.buttons.tool.activeMouseOver.fill'
            },
            '&:focus': {
              backgroundColor: 'geowebColors.buttons.tool.activeMouseOver.fill'
            }
          },
          '&:after': {
            background: 'none!important'
          },
          '&.Mui-disabled': {
            fontSize: '12px',
            opacity: 0.67,
            padding: '0px 12px',
            '&.MuiMenuItem-root': {
              minHeight: '30px!important'
            }
          }
        }
      }
    }
  }, otherProps)));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
var DEFAULT_ERROR_MESSAGE = 'An error has occurred'; // Guard function for detecting an AxiosError (typically the kind of error returned by the backend)

var isAxiosError = function isAxiosError(error) {
  return error.isAxiosError !== undefined;
};
var getAxiosErrorMessage = function getAxiosErrorMessage(error) {
  var data = error.response && error.response.data;

  if (data && typeof data === 'string') {
    return data;
  }

  if (data && data.message && typeof data.message === 'string') {
    return data.message;
  }

  if (error.message) {
    return error.message;
  }

  return DEFAULT_ERROR_MESSAGE;
};
var getErrorMessage = function getErrorMessage(error) {
  if (isAxiosError(error)) {
    return getAxiosErrorMessage(error);
  }

  return error.message || DEFAULT_ERROR_MESSAGE;
};

export { AlertBanner, AlertIcon, AppHeader, AppLayout, Avatar, ConfirmationDialog, ConfirmationServiceProvider, CustomSlider, CustomToggleButton, DEFAULT_ERROR_MESSAGE, DraggableComponent$1 as Draggable, EnableButton, ErrorBoundary, HIDDEN_INPUT_HELPER_IS_DRAFT, ManagerButton, ManagerDeleteButton, ResizeComponent as Resizable, StatusTag, ToggleMenu, ToolButton, ToolContainer, DraggableComponent as ToolContainerDraggable, ToolFooter, ToolHeader, TooltipSelect, UserMenu, UserMenuItemCheatSheet, MenuItemTheme as UserMenuItemTheme, calculateDialogSizeAndPosition, getAxiosErrorMessage, getErrorMessage, getInitials, getPosition, isAxiosError, showPrompt, sliderStyles, tooltipContainerStyles, useConfirmationDialog, useDialogActions, useDraftFormHelpers, useIsMounted, usePoller, usePreventBrowserClose, usePrevious, useUserMenu };
